/**
 * src/lib/sat/taxonomy.ts
 *
 * Single source of truth for the UnlimitedTests SAT taxonomy. Every
 * Question in the bank should map to exactly one topic from this
 * file. User-facing UI (drill picker, score reports, navigation,
 * tutor context) reads from here. The AI question generator reads
 * from here. The audit + prune scripts read from here.
 *
 * Why this file exists:
 *   The bank historically accumulated three distinct taxonomies
 *   from different seeders (College Board's published skill points,
 *   simplified textbook names, and umbrella combinations). The
 *   May 2026 audit revealed 60+ distinct topic strings, including
 *   case-sensitive duplicates ("Linear Functions" vs
 *   "Linear functions"). This module forces a single canonical
 *   shape going forward.
 *
 * Structure:
 *   Test (SAT)
 *     └─ Section (RW | MATH)
 *          └─ Domain (4 per section, official names)
 *               └─ Topic (30 total — 10 RW + 19 official Math + 1
 *                          UnlimitedTests-only "Coordinate Geometry")
 *                    └─ Subskill (optional, finer routing)
 *
 * Conventions:
 *   - All topic names are Title Case (matches RW labels and our
 *     product convention).
 *   - 29 of the 30 topics match College Board's 2024+ digital SAT
 *     scoring spec exactly. "Coordinate Geometry" is a
 *     UnlimitedTests-only topic (not in College Board scoring).
 *     Questions with topic="Coordinate Geometry" must set
 *     `parentTopic` to the College Board topic they'd score under,
 *     so score reports can roll up correctly.
 *   - Subskills are internal tags. They're displayed in question
 *     metadata and the tutor uses them, but score reports and the
 *     primary drill UI use topics.
 */

export type SatSectionCode = 'RW' | 'MATH'

export type SatDomain =
  // Reading and Writing
  | 'Information and Ideas'
  | 'Craft and Structure'
  | 'Expression of Ideas'
  | 'Standard English Conventions'
  // Math
  | 'Algebra'
  | 'Advanced Math'
  | 'Problem-Solving and Data Analysis'
  | 'Geometry and Trigonometry'

export type DomainSpec = {
  section: SatSectionCode
  domain: SatDomain
  /** Approximate proportion of the section. Sums to ~1.0 within a section. */
  weightApprox: number
  topics: TopicSpec[]
}

export type TopicSpec = {
  /** Canonical topic name (Title Case). Unique across the entire taxonomy. */
  topic: string
  /**
   * If true, this topic is NOT in College Board's scoring spec — it's
   * a UnlimitedTests addition for student-friendly drilling. Each
   * question with this topic must also set `parentTopic` to a
   * canonical College Board topic so score rollups stay official.
   */
  unlimitedTestsOnly?: boolean
  /**
   * Subskill labels surfaced via question metadata. NOT
   * user-facing in score reports. Used by the AI tutor and
   * targeted-practice flows.
   */
  subskills: string[]
}

export const SAT_TAXONOMY: DomainSpec[] = [
  // ============================================================
  // Reading and Writing
  // ============================================================
  {
    section: 'RW',
    domain: 'Information and Ideas',
    weightApprox: 0.26,
    topics: [
      {
        topic: 'Central Ideas and Details',
        subskills: ['Main Idea', 'Supporting Details', 'Summarizing'],
      },
      {
        topic: 'Command of Evidence',
        subskills: ['Textual Evidence', 'Quantitative Evidence'],
      },
      {
        topic: 'Inferences',
        subskills: ['Logical Inference', 'Implied Meaning'],
      },
    ],
  },
  {
    section: 'RW',
    domain: 'Craft and Structure',
    weightApprox: 0.28,
    topics: [
      {
        topic: 'Words in Context',
        subskills: ['Vocabulary in Context', 'Word Choice', 'Connotation and Tone'],
      },
      {
        topic: 'Text Structure and Purpose',
        subskills: ["Author's Purpose", 'Text Organization', 'Sentence and Paragraph Function'],
      },
      {
        topic: 'Cross-Text Connections',
        subskills: ['Comparing Perspectives', 'Synthesizing Across Texts'],
      },
    ],
  },
  {
    section: 'RW',
    domain: 'Expression of Ideas',
    weightApprox: 0.2,
    topics: [
      {
        topic: 'Rhetorical Synthesis',
        subskills: ['Synthesizing Notes', 'Argument Construction'],
      },
      {
        topic: 'Transitions',
        subskills: [
          'Logical Transitions',
          'Causal Transitions',
          'Contrast Transitions',
          'Sequence Transitions',
        ],
      },
    ],
  },
  {
    section: 'RW',
    domain: 'Standard English Conventions',
    weightApprox: 0.26,
    topics: [
      {
        topic: 'Boundaries',
        subskills: [
          'End Punctuation',
          'Comma Splices',
          'Semicolons',
          'Colons',
          'Apostrophes',
          'Sentence Boundaries',
          'Punctuation',
          // College Board's official term for non-restrictive appositives /
          // parentheticals / supplementary phrases (paired commas/dashes around
          // an inserted element). Added in SAT-RW-BOUNDARIES-DIVERSITY Stage
          // 2a/2c (#598 detection rule + #600 framework + this PR's backfill +
          // 1–2/module SEC enforcement).
          'Supplementary Elements',
        ],
      },
      {
        topic: 'Form, Structure, and Sense',
        subskills: [
          'Subject-Verb Agreement',
          'Pronoun-Antecedent Agreement',
          'Verb Tense',
          'Modifier Placement',
          'Parallel Structure',
        ],
      },
    ],
  },

  // ============================================================
  // Math
  // ============================================================
  {
    section: 'MATH',
    domain: 'Algebra',
    weightApprox: 0.35,
    topics: [
      {
        topic: 'Linear Equations in 1 Variable',
        subskills: ['Solving for a Variable', 'Word Problems', 'Linear Models'],
      },
      {
        topic: 'Linear Equations in 2 Variables',
        subskills: ['Slope-Intercept Form', 'Point-Slope Form', 'Standard Form', 'Graphing'],
      },
      {
        topic: 'Linear Functions',
        subskills: ['Function Notation', 'Domain and Range', 'Linear Modeling'],
      },
      {
        topic: 'Systems of 2 Linear Equations in 2 Variables',
        subskills: [
          'Substitution Method',
          'Elimination Method',
          'One Solution',
          'Infinite Solutions',
          'No Solution',
        ],
      },
      {
        topic: 'Linear Inequalities in 1 or 2 Variables',
        subskills: [
          'Single-Variable Inequalities',
          'Compound Inequalities',
          'Systems of Inequalities',
          'Graphing on Number Line',
          'Graphing on Coordinate Plane',
        ],
      },
    ],
  },
  {
    section: 'MATH',
    domain: 'Advanced Math',
    weightApprox: 0.35,
    topics: [
      {
        topic: 'Equivalent Expressions',
        subskills: [
          'Factoring',
          'Polynomial Expansion',
          'Rational Expressions',
          'Exponents and Radicals',
          'Difference of Squares',
        ],
      },
      {
        topic: 'Nonlinear Equations in 1 Variable and Systems of Equations in 2 Variables',
        subskills: [
          'Quadratic Equations',
          'Polynomial Equations',
          'Radical Equations',
          'Rational Equations',
          'Absolute Value Equations',
          'Nonlinear Systems',
        ],
      },
      {
        topic: 'Nonlinear Functions',
        subskills: [
          'Quadratic Functions',
          'Polynomial Functions',
          'Exponential Functions',
          'Function Transformations',
          'Function Notation',
        ],
      },
    ],
  },
  {
    section: 'MATH',
    domain: 'Problem-Solving and Data Analysis',
    weightApprox: 0.15,
    topics: [
      {
        topic: 'Ratios, Rates, Proportional Relationships, and Units',
        subskills: [
          'Ratio Problems',
          'Rate Problems',
          'Unit Conversion',
          'Direct Proportion',
          'Inverse Proportion',
        ],
      },
      {
        topic: 'Percentages',
        subskills: [
          'Percent Change',
          'Percent of a Number',
          'Compound Interest',
          'Discount and Markup',
        ],
      },
      {
        topic: 'One-Variable Data: Distributions and Measures of Center and Spread',
        subskills: [
          'Mean',
          'Median',
          'Mode',
          'Range',
          'Standard Deviation',
          'Box Plots',
          'Histograms',
        ],
      },
      {
        topic: 'Two-Variable Data: Models and Scatterplots',
        subskills: [
          'Scatterplot Interpretation',
          'Linear Models',
          'Correlation',
          'Line of Best Fit',
          'Residuals',
        ],
      },
      {
        topic: 'Probability and Conditional Probability',
        subskills: [
          'Simple Probability',
          'Conditional Probability',
          'Independent Events',
          'Compound Events',
          'Two-Way Tables',
        ],
      },
      {
        topic: 'Inference from Sample Statistics and Margin of Error',
        subskills: ['Confidence Intervals', 'Margin of Error', 'Sample Size Effects'],
      },
      {
        topic: 'Evaluating Statistical Claims: Observational Studies and Experiments',
        subskills: [
          'Observational vs Experimental',
          'Causation vs Correlation',
          'Bias Detection',
          'Generalization Limits',
          // Study-design subskill covering simple random / stratified /
          // cluster / systematic sampling. Added 2026-05-07 after a
          // stratified-sampling question got stuck at low confidence in
          // the Phase 3 reclassifier — the existing four subskills
          // didn't capture "identify the sampling method" cleanly.
          'Sampling Methods',
        ],
      },
    ],
  },
  {
    section: 'MATH',
    domain: 'Geometry and Trigonometry',
    weightApprox: 0.15,
    topics: [
      {
        topic: 'Area and Volume',
        subskills: [
          'Triangle Area',
          'Circle Area',
          'Polygon Area',
          'Prism Volume',
          'Cylinder Volume',
          'Cone Volume',
          'Sphere Volume',
          'Surface Area',
        ],
      },
      {
        topic: 'Lines, Angles, and Triangles',
        subskills: [
          'Angle Relationships',
          'Parallel Lines',
          'Triangle Properties',
          'Similar Triangles',
          'Congruent Triangles',
        ],
      },
      {
        topic: 'Right Triangles and Trigonometry',
        subskills: [
          'Pythagorean Theorem',
          'Special Right Triangles',
          'Sine, Cosine, Tangent',
          'Trig Identities',
          'Unit Circle',
        ],
      },
      {
        topic: 'Circles',
        subskills: [
          'Circumference and Area',
          'Arc Length',
          'Sector Area',
          'Equation of a Circle',
          'Inscribed Angles',
          'Tangent Lines',
          // Covers chord-related theorems: Intersecting Chords (power
          // of point inside circle), Tangent-Secant, Secant-Secant,
          // and chord-arc angle relationships. Added 2026-05-10 after
          // an intersecting-chords question got stuck at low confidence
          // in the Phase 4 reclassifier — the existing six subskills
          // didn't capture chord-segment-product theorems.
          'Chord Relationships',
        ],
      },
      {
        topic: 'Coordinate Geometry',
        unlimitedTestsOnly: true,
        subskills: [
          'Distance Formula',
          'Midpoint Formula',
          'Slope',
          'Lines on the Plane',
          'Equation of a Circle in Coordinate Plane',
          'Intersections',
          'Reflections and Translations',
        ],
      },
    ],
  },
]

// ============================================================
// Derived data + helpers
// ============================================================

/** All canonical topic names, in the order they appear in SAT_TAXONOMY. */
export const CANONICAL_TOPICS: string[] = SAT_TAXONOMY.flatMap((d) => d.topics.map((t) => t.topic))

/** All College Board scoring topics (excludes the UnlimitedTests-only ones). */
export const COLLEGE_BOARD_TOPICS: string[] = SAT_TAXONOMY.flatMap((d) =>
  d.topics.filter((t) => !t.unlimitedTestsOnly).map((t) => t.topic),
)

/** All UnlimitedTests-only (non-canonical-at-CB) topics. */
export const UT_ONLY_TOPICS: string[] = SAT_TAXONOMY.flatMap((d) =>
  d.topics.filter((t) => t.unlimitedTestsOnly === true).map((t) => t.topic),
)

const TOPIC_TO_DOMAIN_LOOKUP: Record<string, DomainSpec> = (() => {
  const map: Record<string, DomainSpec> = {}
  for (const d of SAT_TAXONOMY) {
    for (const t of d.topics) {
      map[t.topic] = d
    }
  }
  return map
})()

const TOPIC_TO_SPEC_LOOKUP: Record<string, TopicSpec> = (() => {
  const map: Record<string, TopicSpec> = {}
  for (const d of SAT_TAXONOMY) {
    for (const t of d.topics) {
      map[t.topic] = t
    }
  }
  return map
})()

/**
 * Whether a string is one of the 28 canonical topic names.
 * Case-sensitive — callers should normalize before comparing if
 * they accept user input.
 */
export function isCanonicalTopic(topic: string): boolean {
  return Object.prototype.hasOwnProperty.call(TOPIC_TO_SPEC_LOOKUP, topic)
}

/** Returns the domain a topic belongs to, or null if unknown. */
export function domainForTopic(topic: string): SatDomain | null {
  return TOPIC_TO_DOMAIN_LOOKUP[topic]?.domain ?? null
}

/** Returns the section ("RW" or "MATH") for a topic, or null if unknown. */
export function sectionForTopic(topic: string): SatSectionCode | null {
  return TOPIC_TO_DOMAIN_LOOKUP[topic]?.section ?? null
}

/**
 * All canonical (Title Case) bank topics that belong to a domain. Used to
 * launch a DOMAIN-scoped practice session — e.g. the Mastery Map's coarse
 * "Geometry" / "Algebra" umbrella cells (historical attempts recorded under a
 * bare domain label that is no longer a live Question.topic) route to
 * `?domain=<domain>`, which the practice/start route resolves to these topics
 * so the student practices the whole domain instead of dead-ending on the
 * section-wide "General Math Practice" fallback. Returns [] for an unknown
 * domain (including "Other"), so callers can treat empty as "not a real domain".
 */
export function topicsForDomain(domain: SatDomain): string[] {
  return SAT_TAXONOMY.filter((d) => d.domain === domain).flatMap((d) =>
    d.topics.map((t) => t.topic),
  )
}

/** Returns the topic spec (subskills, flags) for a topic, or null. */
export function topicSpec(topic: string): TopicSpec | null {
  return TOPIC_TO_SPEC_LOOKUP[topic] ?? null
}

/** Returns the subskills list for a topic, or [] if unknown. */
export function subskillsForTopic(topic: string): string[] {
  return TOPIC_TO_SPEC_LOOKUP[topic]?.subskills ?? []
}

/**
 * Whether a (topic, subskill) pair is valid — the topic exists
 * and the subskill is one of its declared subskills. Used to
 * reject malformed values at insert time.
 */
export function isValidSubskill(topic: string, subskill: string): boolean {
  const spec = TOPIC_TO_SPEC_LOOKUP[topic]
  if (!spec) return false
  return spec.subskills.includes(subskill)
}

/**
 * Whether a topic is UnlimitedTests-only (not in CB scoring).
 * Such topics require `parentTopic` to be set on the question.
 */
export function isUnlimitedTestsOnlyTopic(topic: string): boolean {
  return TOPIC_TO_SPEC_LOOKUP[topic]?.unlimitedTestsOnly === true
}

/**
 * Recommended count distribution: takes a target total and returns
 * how many questions to generate per topic, weighted by the domain
 * proportions and split evenly within each domain. Used by the AI
 * generator's distribution plan.
 */
export function distributionForTotal(
  total: number,
  section?: SatSectionCode,
): { section: SatSectionCode; domain: SatDomain; topic: string; count: number }[] {
  const domains = section ? SAT_TAXONOMY.filter((d) => d.section === section) : SAT_TAXONOMY
  const totalWeight = domains.reduce((s, d) => s + d.weightApprox, 0)

  const out: {
    section: SatSectionCode
    domain: SatDomain
    topic: string
    count: number
  }[] = []
  for (const d of domains) {
    const domainTotal = Math.round((total * d.weightApprox) / totalWeight)
    const perTopic = Math.max(1, Math.floor(domainTotal / d.topics.length))
    let leftover = domainTotal - perTopic * d.topics.length
    for (const t of d.topics) {
      // Distribute leftover one at a time so totals match exactly.
      const extra = leftover > 0 ? 1 : 0
      if (leftover > 0) leftover--
      out.push({
        section: d.section,
        domain: d.domain,
        topic: t.topic,
        count: perTopic + extra,
      })
    }
  }
  return out
}
